import React, { useState, useEffect, useRef } from 'react';
import { SearchTabsProps, SearchTabProps } from './../../../types/search';

const SearchTab = ({
  tab,
  onTabClick,
  isActive,
  activeTotal,
  savedCount,
}: SearchTabProps & {
  isActive: boolean;
  activeTotal?: number;
  savedCount?: number | string;
}) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onTabClick(tab);
  };

  const getDisplayTotal = () => {
    if (isActive && savedCount !== undefined) {
      return savedCount;
    }
    if (isActive && activeTotal !== undefined) {
      return activeTotal;
    }
    return tab.total;
  };

  const displayTotal = getDisplayTotal();
  const numericTotal = typeof displayTotal === 'string' ? parseInt(displayTotal, 10) : displayTotal;

  return (
    <>
      {numericTotal > 0 && (
        <button
          type="button"
          role="tab"
          aria-selected={isActive}
          className={`search__tab-item ${isActive ? 'search__tab-item--selected' : ''}`}
          onClick={handleClick}
        >
          <span>{tab.heading}</span> <span className="search__tab-total">({displayTotal})</span>
        </button>
      )}
    </>
  );
};

const SearchTabs = ({
  tabs,
  onTabClick,
  activeTabKey,
  summary,
  isLoading,
}: SearchTabsProps & {
  summary?: { total?: number };
  isLoading?: boolean;
}) => {
  const [tabCounts, setTabCounts] = useState<Record<string, number | string>>({});
  const prevActiveKey = useRef<string | null>(null);
  const isLoadingRef = useRef(isLoading);

  useEffect(() => {
    if (isLoadingRef.current && !isLoading) {
      if (activeTabKey) {
        setTabCounts(prev => {
          const newCounts = { ...prev };
          delete newCounts[activeTabKey];
          return newCounts;
        });
      }
    }
    isLoadingRef.current = isLoading;
  }, [isLoading, activeTabKey]);

  useEffect(() => {
    if (summary?.total !== undefined && activeTabKey && !isLoading) {
      setTabCounts(prev => {
        const newCounts = { ...prev };
        delete newCounts[activeTabKey];
        return newCounts;
      });
    }
  }, [summary, activeTabKey, isLoading]);

  const handleTabClick = (tab: any) => {
    if (tab.key !== activeTabKey) {
      setTabCounts(prev => ({
        ...prev,
        [tab.key]: tab.total,
      }));
      prevActiveKey.current = tab.key;
    }
    onTabClick(tab);
  };

  return (
    <>
      {tabs.map(tab => (
        <SearchTab
          key={tab.key}
          tab={tab}
          onTabClick={handleTabClick}
          isActive={tab.key === activeTabKey || tab.isSelected === true}
          activeTotal={summary?.total}
          savedCount={tab.key === activeTabKey ? tabCounts[tab.key] : undefined}
        />
      ))}
    </>
  );
};

export default SearchTabs;
